import { randomUUID } from "node:crypto";
import { EventEmitter } from "node:events";
import { PROTOCOL_VERSION, type EventFrame, type HelloOk, type ResponseFrame } from "@mewbleh/nori-protocol";
import WebSocket from "ws";

type Pending = { resolve(value: unknown): void; reject(reason: Error): void; timeout: NodeJS.Timeout };

export class GatewayClient extends EventEmitter {
  #socket?: WebSocket;
  readonly #pending = new Map<string, Pending>();
  hello?: HelloOk;

  constructor(
    readonly url: string,
    readonly token: string,
    readonly version = "0.3.0",
    readonly scopes: string[] = ["operator.read", "operator.write", "operator.admin", "operator.approvals", "operator.pairing"],
  ) {
    super();
  }

  async connect(): Promise<HelloOk> {
    const socket = new WebSocket(this.url, { maxPayload: 25 * 1024 * 1024 });
    this.#socket = socket;
    socket.on("message", (data) => this.#onMessage(data));
    socket.on("close", () => {
      this.#rejectAll(new Error("Gateway connection closed"));
      this.emit("close");
    });
    socket.on("error", (error) => {
      this.#rejectAll(error);
      this.emit("socketError", error);
    });
    const challenge = new Promise<void>((resolve, reject) => {
      const cleanup = () => { clearTimeout(timeout); socket.off("message", onMessage); };
      const onMessage = (data: WebSocket.RawData) => {
        try {
          const frame = JSON.parse(data.toString()) as EventFrame;
          if (frame.type !== "event" || frame.event !== "connect.challenge") return;
          cleanup();
          resolve();
        } catch (error) {
          cleanup();
          reject(new Error(`Invalid Gateway handshake: ${(error as Error).message}`));
        }
      };
      const timeout = setTimeout(() => { cleanup(); reject(new Error("Gateway handshake timed out")); }, 15_000);
      socket.on("message", onMessage);
    });
    const opened = new Promise<void>((resolve, reject) => {
      const onOpen = () => { socket.off("error", onError); resolve(); };
      const onError = (error: Error) => { socket.off("open", onOpen); reject(error); };
      socket.once("open", onOpen);
      socket.once("error", onError);
    });
    await Promise.all([opened, challenge]);
    const hello = (await this.request("connect", {
      minProtocol: PROTOCOL_VERSION,
      maxProtocol: PROTOCOL_VERSION,
      client: { id: "cli", displayName: "Nori CLI", version: this.version, platform: process.platform, mode: "cli" },
      role: "operator",
      scopes: this.scopes,
      auth: { token: this.token },
      caps: ["tool-events"],
      locale: Intl.DateTimeFormat().resolvedOptions().locale,
      userAgent: `nori-cli/${this.version}`,
    })) as HelloOk;
    this.hello = hello;
    return hello;
  }

  #onMessage(data: WebSocket.RawData): void {
    let frame: ResponseFrame | EventFrame;
    try {
      frame = JSON.parse(data.toString()) as ResponseFrame | EventFrame;
    } catch (error) {
      this.#rejectAll(new Error(`Invalid JSON from Gateway: ${(error as Error).message}`));
      this.#socket?.close(1002, "invalid JSON");
      return;
    }
    if (frame.type === "event") {
      this.emit("event", frame);
      this.emit(frame.event, frame.payload);
      return;
    }
    const pending = this.#pending.get(frame.id);
    if (!pending) return;
    this.#pending.delete(frame.id);
    clearTimeout(pending.timeout);
    if (frame.ok) pending.resolve(frame.payload);
    else pending.reject(Object.assign(new Error(frame.error?.message ?? "Gateway request failed"), frame.error));
  }

  request(method: string, params?: unknown, timeoutMs = 30_000): Promise<unknown> {
    const socket = this.#socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return Promise.reject(new Error("Gateway is not connected"));
    const id = randomUUID();
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.#pending.delete(id);
        reject(new Error(`Gateway request timed out: ${method}`));
      }, timeoutMs);
      this.#pending.set(id, { resolve, reject, timeout });
      try {
        socket.send(JSON.stringify({ type: "req", id, method, ...(params === undefined ? {} : { params }) }), (error) => {
          if (!error) return;
          const pending = this.#pending.get(id);
          if (!pending) return;
          this.#pending.delete(id);
          clearTimeout(pending.timeout);
          pending.reject(error);
        });
      } catch (error) {
        this.#pending.delete(id);
        clearTimeout(timeout);
        reject(error);
      }
    });
  }

  #rejectAll(error: Error): void {
    for (const pending of this.#pending.values()) {
      clearTimeout(pending.timeout);
      pending.reject(error);
    }
    this.#pending.clear();
  }

  close(): void {
    this.#socket?.close();
  }
}
